import { timing, variantForLevel } from './params';
export const FIELD_SIZE = 280;
const SPHERE_RADIUS = 16;
const FIELD_MARGIN = SPHERE_RADIUS + 6;
/** Frequence de rafraichissement de la phase motion (ms entre frames). */
const MOTION_TICK_MS = 80;
/** Conversion vitesse NeuroTracker units -> px / tick (calibration MVP). */
const SPEED_PX_PER_UNIT_PER_SEC = 28;
/** Petit LCG deterministe seed-base, retourne float ∈ [0, 1). */
function lcg(seed) {
    let s = (seed | 0) || 1;
    return () => {
        s = (s * 1664525 + 1013904223) >>> 0;
        return s / 4294967296;
    };
}
/**
 * Genere les positions initiales + cibles deterministes pour un trialIdx donne.
 * Garantit replay test-friendly.
 */
export function buildStimulusPayload(trialIdx, speedUnits, variant) {
    const rng = lcg(trialIdx + 1);
    const sphereSeeds = [];
    for (let i = 0; i < variant.nSpheres; i += 1) {
        sphereSeeds.push(Math.floor(rng() * 1_000_000));
    }
    // Selection cibles : Fisher-Yates partiel sur indices 0..nSpheres-1.
    const indices = Array.from({ length: variant.nSpheres }, (_, i) => i);
    for (let i = indices.length - 1; i > 0; i -= 1) {
        const j = Math.floor(rng() * (i + 1));
        const a = indices[i];
        const b = indices[j];
        indices[i] = b;
        indices[j] = a;
    }
    const targetIndices = indices.slice(0, variant.nTargets).sort((a, b) => a - b);
    return {
        trialIdx,
        speedUnits,
        nSpheres: variant.nSpheres,
        nTargets: variant.nTargets,
        sphereSeeds,
        targetIndices,
    };
}
/** Position initiale d'une sphere derivee de son seed. */
function initialPositions(sphereSeeds) {
    return sphereSeeds.map((seed, idx) => {
        const rng = lcg(seed + 7);
        const x = FIELD_MARGIN + rng() * (FIELD_SIZE - FIELD_MARGIN * 2);
        const y = FIELD_MARGIN + rng() * (FIELD_SIZE - FIELD_MARGIN * 2);
        const z = 0.7 + rng() * 0.6;
        return { idx, x, y, z };
    });
}
/** Avance une sphere d'un tick (rebond aux bords). */
function stepPosition(pos, velocity, dtMs) {
    const dt = dtMs / 1000;
    let { vx, vy } = velocity;
    let nx = pos.x + vx * dt;
    let ny = pos.y + vy * dt;
    if (nx < FIELD_MARGIN) {
        nx = FIELD_MARGIN;
        vx = -vx;
    }
    else if (nx > FIELD_SIZE - FIELD_MARGIN) {
        nx = FIELD_SIZE - FIELD_MARGIN;
        vx = -vx;
    }
    if (ny < FIELD_MARGIN) {
        ny = FIELD_MARGIN;
        vy = -vy;
    }
    else if (ny > FIELD_SIZE - FIELD_MARGIN) {
        ny = FIELD_SIZE - FIELD_MARGIN;
        vy = -vy;
    }
    return { pos: { ...pos, x: nx, y: ny }, velocity: { vx, vy } };
}
export function createThreeDMotAdapter(level = 1) {
    let state = {
        subPhase: 'idle',
        stim: null,
        feedback: null,
        level,
        variant: variantForLevel(level),
        positions: [],
        picked: [],
    };
    const listeners = new Set();
    let resolver = null;
    let rejecter = null;
    let trialStart = 0;
    const timers = [];
    let motionInterval = null;
    function clearAllTimers() {
        while (timers.length > 0) {
            const t = timers.pop();
            if (t !== undefined)
                clearTimeout(t);
        }
        if (motionInterval !== null) {
            clearInterval(motionInterval);
            motionInterval = null;
        }
    }
    function update(patch) {
        state = { ...state, ...patch };
        for (const l of listeners) {
            try {
                l(state);
            }
            catch {
                /* ignored */
            }
        }
    }
    function startMotion(stim) {
        const positions = initialPositions(stim.sphereSeeds);
        const speedPxPerSec = stim.speedUnits * SPEED_PX_PER_UNIT_PER_SEC;
        const velocities = stim.sphereSeeds.map((seed) => {
            const rng = lcg(seed + 13);
            const angle = rng() * Math.PI * 2;
            return {
                vx: Math.cos(angle) * speedPxPerSec,
                vy: Math.sin(angle) * speedPxPerSec,
            };
        });
        update({ subPhase: 'motion', positions });
        motionInterval = setInterval(() => {
            const next = [];
            for (let i = 0; i < positions.length; i += 1) {
                const v = velocities[i];
                const p = positions[i];
                const { pos: np, velocity: nv } = stepPosition(p, v, MOTION_TICK_MS);
                positions[i] = np;
                velocities[i] = nv;
                next.push(np);
            }
            update({ positions: next });
        }, MOTION_TICK_MS);
    }
    return {
        mount() {
            // Idempotent — render purement declaratif cote React.
        },
        unmount() {
            clearAllTimers();
            listeners.clear();
            if (rejecter) {
                rejecter(new Error('unmount'));
                rejecter = null;
                resolver = null;
            }
        },
        presentStimulus(stim) {
            clearAllTimers();
            return new Promise((resolve, reject) => {
                resolver = resolve;
                rejecter = reject;
                const positions = initialPositions(stim.sphereSeeds);
                update({
                    subPhase: 'fixation',
                    stim,
                    feedback: null,
                    positions,
                    picked: [],
                });
                timers.push(setTimeout(() => {
                    // Phase indication : cibles flashent rouge.
                    update({ subPhase: 'indication' });
                    timers.push(setTimeout(() => {
                        // Phase motion : positions animees jusqu'a motionMs.
                        startMotion(stim);
                        trialStart = Date.now();
                        timers.push(setTimeout(() => {
                            if (motionInterval !== null) {
                                clearInterval(motionInterval);
                                motionInterval = null;
                            }
                            update({ subPhase: 'freeze' });
                            timers.push(setTimeout(() => {
                                update({ subPhase: 'awaiting_input' });
                            }, timing.freezeMs));
                        }, timing.motionMs));
                    }, timing.indicationMs));
                }, timing.fixationMs));
            });
        },
        async showFeedback(correct) {
            update({
                subPhase: 'feedback',
                feedback: correct ? 'correct' : 'incorrect',
            });
            await new Promise((resolve) => {
                timers.push(setTimeout(() => {
                    update({ subPhase: 'iti' });
                    resolve();
                }, timing.feedbackMs));
            });
        },
        pause() {
            // Sprint 0.5 : no-op.
        },
        resume() {
            // Sprint 0.5 : no-op.
        },
        abort() {
            clearAllTimers();
            if (rejecter) {
                rejecter(new Error('aborted'));
                rejecter = null;
                resolver = null;
            }
        },
        getState() {
            return state;
        },
        subscribe(listener) {
            listeners.add(listener);
            listener(state);
            return () => {
                listeners.delete(listener);
            };
        },
        handlePick(idx) {
            if (state.subPhase !== 'awaiting_input')
                return;
            const stim = state.stim;
            if (!stim)
                return;
            // Toggle : un click sur une sphere deja picked la deselectionne.
            let nextPicked;
            if (state.picked.includes(idx)) {
                nextPicked = state.picked.filter((i) => i !== idx);
                update({ picked: nextPicked });
                return;
            }
            nextPicked = [...state.picked, idx];
            update({ picked: nextPicked });
            if (nextPicked.length < stim.nTargets)
                return;
            // N picks atteints — on resout la Promise.
            const targetSet = new Set(stim.targetIndices);
            const allCorrect = nextPicked.length === stim.nTargets &&
                nextPicked.every((i) => targetSet.has(i));
            const rt = Date.now() - trialStart;
            const resp = {
                pickedIndices: nextPicked,
                reactionTimeMs: rt,
                allCorrect,
            };
            if (resolver) {
                const r = resolver;
                resolver = null;
                rejecter = null;
                r(resp);
            }
        },
        setLevel(newLevel) {
            const v = variantForLevel(newLevel);
            update({ level: newLevel, variant: v });
        },
    };
}
//# sourceMappingURL=adapter.js.map